<template>
    <section :class="['compliance-dashboard', { dark: isDark }]">
        <div class="dashboard-page-header">
            <div>
                <p class="dashboard-eyebrow">Focused Dashboard</p>
                <h1>Compliance & Data Quality</h1>
                <p class="dashboard-summary">
                    Actionable local evidence for AGCO processing, store assignment, test-product leakage, and stock-import freshness.
                </p>
            </div>
            <div class="dashboard-header-actions">
                <span class="source-pill">Local evidence</span>
                <router-link class="dashboard-link" to="/agco-logs/warehouses">
                    View AGCO logs
                    <i class="material-icons">arrow_forward</i>
                </router-link>
            </div>
        </div>

        <section class="verification-notice" role="status" aria-label="AGCO external verification status">
            <i class="material-icons">cloud_off</i>
            <div>
                <strong>External AGCO verification unavailable</strong>
                <p>
                    Submission statuses below come from local processing logs only. They do not confirm that AGCO accepted a report.
                </p>
            </div>
        </section>

        <div class="status-grid">
            <article v-for="card in statusCards" :key="card.label" :class="['status-card', card.tone]">
                <div class="status-icon">
                    <i class="material-icons">{{ card.icon }}</i>
                </div>
                <div>
                    <p>{{ card.label }}</p>
                    <h2>{{ card.value }}</h2>
                    <span>{{ card.helper }}</span>
                </div>
            </article>
        </div>

        <section class="dashboard-section">
            <div class="section-header">
                <div>
                    <p class="dashboard-eyebrow">Exception Overview</p>
                    <h2>Checks that need operational review</h2>
                </div>
                <span class="section-count">{{ activeIssueCount }} open checks</span>
            </div>

            <div v-if="loading" class="loading-state">Loading compliance checks...</div>
            <div v-else class="visual-grid">
                <article class="visual-card">
                    <div class="visual-card-header">
                        <div>
                            <h3>Local AGCO exceptions by store</h3>
                            <p>Failed, overdue, or missing local inventory and position events. QA seed rows are excluded.</p>
                        </div>
                    </div>
                    <div class="visual-frame">
                        <EChartsHorizontalBarChart
                            v-if="agcoIssueChartCategories.length"
                            :categories="agcoIssueChartCategories"
                            :values="agcoIssueChartValues"
                            series-name="Issue events"
                            aria-label="AGCO issue count by store"
                            :is-dark="isDark"
                            :value-formatter="formatInteger"
                        />
                        <div v-else class="empty-state">No AGCO issue events are currently visible.</div>
                    </div>
                </article>

                <article class="visual-card">
                    <div class="visual-card-header">
                        <div>
                            <h3>Data quality issue mix</h3>
                            <p>Current cleanup demand across local reporting, store assignment, and product naming.</p>
                        </div>
                    </div>
                    <div class="visual-frame">
                        <EChartsHorizontalBarChart
                            :categories="qualityIssueCategories"
                            :values="qualityIssueValues"
                            series-name="Open items"
                            aria-label="Data quality issue mix"
                            :is-dark="isDark"
                            :value-formatter="formatInteger"
                        />
                    </div>
                </article>
            </div>
        </section>

        <div class="detail-grid">
            <section class="dashboard-section agco-section">
                <div class="section-header">
                    <div>
                        <p class="dashboard-eyebrow">AGCO Submissions</p>
                        <h2>Store local-processing status</h2>
                    </div>
                    <router-link class="mini-link" to="/agco-logs/warehouses">Logs</router-link>
                </div>

                <div v-if="errors.agco" class="inline-error">AGCO health could not load.</div>
                <table v-else class="status-table">
                    <thead>
                        <tr>
                            <th>Store</th>
                            <th>Reporting</th>
                            <th>Inventory</th>
                            <th>Position</th>
                            <th>Latest failure</th>
                            <th>Last attempt</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="store in agcoRows" :key="store.id">
                            <td>{{ store.warehouseName }}</td>
                            <td><span :class="['status-badge', store.applicability]">{{ store.applicabilityLabel }}</span></td>
                            <td><span :class="['status-badge', store.inventoryStatus]">{{ store.inventoryLabel }}</span></td>
                            <td><span :class="['status-badge', store.positionStatus]">{{ store.positionLabel }}</span></td>
                            <td class="failure-reason">{{ store.failureReason || "—" }}</td>
                            <td>{{ formatDateTime(store.lastAttemptAt) }}</td>
                            <td class="log-links">
                                <router-link :to="`/agco-logs?warehouse_id=${store.id}&is_position_trigger_event=false`">Inventory</router-link>
                                <router-link :to="`/agco-logs?warehouse_id=${store.id}&is_position_trigger_event=true`">Position</router-link>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </section>

            <section class="dashboard-section">
                <div class="section-header">
                    <div>
                        <p class="dashboard-eyebrow">Cleanup Queue</p>
                        <h2>Data quality checks</h2>
                    </div>
                </div>

                <div class="cleanup-list">
                    <router-link
                        v-for="item in cleanupItems"
                        :key="item.id"
                        :to="item.to"
                        :class="['cleanup-item', item.tone]"
                    >
                        <div>
                            <strong>{{ item.title }}</strong>
                            <p>{{ item.description }}</p>
                            <span>{{ item.source }}</span>
                        </div>
                        <i class="material-icons">arrow_forward</i>
                    </router-link>
                </div>
            </section>
        </div>

        <section class="dashboard-section">
            <div class="section-header">
                <div>
                    <p class="dashboard-eyebrow">Import Health</p>
                    <h2>Recent stock entries</h2>
                </div>
                <router-link class="mini-link" to="/import-history">Import history</router-link>
            </div>

            <div v-if="errors.imports" class="inline-error">Import history could not load.</div>
            <table v-else class="status-table">
                <thead>
                    <tr>
                        <th>Entry</th>
                        <th>Created</th>
                        <th>Reference</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="entry in recentImportEntries" :key="entry.id">
                        <td>
                            <router-link :to="`/import-history/${entry.id}`">{{ entry.name || entry.id }}</router-link>
                        </td>
                        <td>{{ formatDate(entry.createdAt || entry.formattedCreatedAt) }}</td>
                        <td>{{ entry.reference || entry.type || "Stock entry" }}</td>
                    </tr>
                    <tr v-if="!recentImportEntries.length">
                        <td colspan="3">No stock import entries found.</td>
                    </tr>
                </tbody>
            </table>
        </section>

        <AnalyticsGoalPanel :is-dark="isDark" active-goal-id="compliance-data-quality" />
    </section>
</template>

<script>
import { format, subDays } from "date-fns"
import { http } from "../../utils/http"
import { mapActions, mapState } from "vuex"
import AnalyticsGoalPanel from "../../components/analytics/AnalyticsGoalPanel.vue"
import EChartsHorizontalBarChart from "../../components/charts/EChartsHorizontalBarChart.vue"
import SalesFetchApi from "../../utils/fetch/sales-fetch-api"
import {
    findTestProducts,
    findUnknownWarehouse,
    summarizeAgcoHealth,
    summarizeImportEntries,
} from "../../utils/compliance-dashboard"

const currencyFormatter = new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
    maximumFractionDigits: 0,
})

const integerFormatter = new Intl.NumberFormat("en-CA", {
    maximumFractionDigits: 0,
})

function getDefaultDateRange() {
    const today = new Date()

    return {
        startDate: format(subDays(today, 6), "yyyy-MM-dd"),
        endDate: format(today, "yyyy-MM-dd"),
    }
}

export default {
    name: "ComplianceDataQualityDashboard",
    components: {
        AnalyticsGoalPanel,
        EChartsHorizontalBarChart,
    },
    data() {
        return {
            loading: true,
            dateRange: getDefaultDateRange(),
            agcoStores: [],
            externalVerification: null,
            salesByWarehouse: [],
            topProducts: [],
            importEntries: [],
            errors: {
                agco: false,
                warehouse: false,
                products: false,
                imports: false,
            },
        }
    },
    computed: {
        ...mapState("misc", ["theme", "THEMES"]),
        isDark() {
            return this.theme === this.THEMES.DARK
        },
        agcoSummary() {
            return summarizeAgcoHealth(this.agcoStores)
        },
        agcoRows() {
            return this.agcoSummary.rows
        },
        unknownWarehouse() {
            return findUnknownWarehouse(this.salesByWarehouse)
        },
        testProducts() {
            return findTestProducts(this.topProducts)
        },
        importSummary() {
            return summarizeImportEntries(this.importEntries)
        },
        recentImportEntries() {
            return this.importSummary.recentEntries
        },
        unknownWarehouseRevenue() {
            return Number(this.unknownWarehouse?.revenue || 0)
        },
        unknownWarehouseOrders() {
            return Number(this.unknownWarehouse?.orders || 0)
        },
        agcoIssueEvents() {
            return this.agcoSummary.failedEvents + this.agcoSummary.overdueEvents + this.agcoSummary.missingEvents
        },
        activeIssueCount() {
            return this.agcoSummary.problemStores + this.agcoSummary.notConfiguredStores
                + (this.unknownWarehouseRevenue > 0 ? 1 : 0) + this.testProducts.length
        },
        statusCards() {
            return [
                {
                    label: "Local AGCO exceptions",
                    value: this.errors.agco ? "Error" : this.formatIntegerValue(this.agcoSummary.problemStores),
                    helper: this.errors.agco
                        ? "Local AGCO status could not load"
                        : `${this.formatIntegerValue(this.agcoIssueEvents)} events across ${this.formatIntegerValue(this.agcoSummary.expectedStores)} expected stores`,
                    icon: "gavel",
                    tone: this.errors.agco || this.agcoSummary.problemStores ? "danger" : "success",
                },
                {
                    label: "AGCO not configured",
                    value: this.errors.agco ? "Error" : this.formatIntegerValue(this.agcoSummary.notConfiguredStores),
                    helper: this.errors.agco
                        ? "Reporting configuration could not load"
                        : `${this.formatIntegerValue(this.agcoSummary.notApplicableStores)} stores marked not applicable`,
                    icon: "settings_alert",
                    tone: this.errors.agco ? "danger" : this.agcoSummary.notConfiguredStores ? "warning" : "success",
                },
                {
                    label: "Unknown warehouse",
                    value: this.errors.warehouse ? "Error" : this.formatCurrencyValue(this.unknownWarehouseRevenue),
                    helper: `${this.formatIntegerValue(this.unknownWarehouseOrders)} orders without a valid store`,
                    icon: "warehouse",
                    tone: this.unknownWarehouseRevenue > 0 ? "danger" : "success",
                },
                {
                    label: "Test products",
                    value: this.errors.products ? "Error" : this.formatIntegerValue(this.testProducts.length),
                    helper: "Test-named products appearing in top sellers",
                    icon: "bug_report",
                    tone: this.testProducts.length ? "warning" : "success",
                },
            ]
        },
        agcoIssueChartRows() {
            return this.agcoRows
                .filter(row => row.issueCount > 0)
                .sort((a, b) => b.issueCount - a.issueCount)
                .slice(0, 8)
        },
        agcoIssueChartCategories() {
            return this.agcoIssueChartRows.map(row => row.warehouseName)
        },
        agcoIssueChartValues() {
            return this.agcoIssueChartRows.map(row => row.issueCount)
        },
        qualityIssueCategories() {
            return ["Local AGCO events", "AGCO not configured", "Unknown warehouse orders", "Test products"]
        },
        qualityIssueValues() {
            return [
                this.agcoIssueEvents,
                this.agcoSummary.notConfiguredStores,
                this.unknownWarehouseOrders,
                this.testProducts.length,
            ]
        },
        cleanupItems() {
            return [
                {
                    id: "agco",
                    title: "AGCO submissions need review",
                    description: this.errors.agco
                        ? "The AGCO health endpoint could not be verified."
                        : `${this.agcoSummary.problemStores} expected stores have failed, overdue, or missing local events; ${this.agcoSummary.notConfiguredStores} are not configured.`,
                    source: "Local logs only · external acceptance not verified",
                    to: "/agco-logs/warehouses",
                    tone: this.agcoSummary.problemStores || this.agcoSummary.notConfiguredStores || this.errors.agco ? "danger" : "success",
                },
                {
                    id: "unknown-warehouse",
                    title: "Sales posted without a store",
                    description: this.errors.warehouse
                        ? "Sales by store could not be verified."
                        : `${this.formatCurrencyValue(this.unknownWarehouseRevenue)} across ${this.formatIntegerValue(this.unknownWarehouseOrders)} orders is assigned to Unknown Warehouse.`,
                    source: "Source: /analytics/sales-by-warehouse",
                    to: "/reports/sale-by-location",
                    tone: this.unknownWarehouseRevenue > 0 || this.errors.warehouse ? "danger" : "success",
                },
                {
                    id: "test-products",
                    title: "Test products in sales data",
                    description: this.errors.products
                        ? "Top product sales could not be verified."
                        : `${this.testProducts.length} test-named products appear in the current top sellers sample.`,
                    source: "Source: /analytics/sales-by-product",
                    to: "/reports/sale-by-product",
                    tone: this.testProducts.length || this.errors.products ? "warning" : "success",
                },
                {
                    id: "imports",
                    title: "Import history freshness",
                    description: this.errors.imports
                        ? "Stock entry history could not be verified."
                        : this.importSummary.latestEntry
                            ? `Latest stock entry was created ${this.formatDate(this.importSummary.latestEntry.createdAt || this.importSummary.latestEntry.formattedCreatedAt)}.`
                            : "No stock import entries were found.",
                    source: "Source: /stock/entries",
                    to: "/import-history",
                    tone: this.errors.imports ? "warning" : "neutral",
                },
            ]
        },
    },
    async mounted() {
        this.setData({ pageTitle: "Compliance & Data Quality" })
        await this.fetchComplianceData()
    },
    methods: {
        ...mapActions("misc", ["setData"]),
        async fetchComplianceData() {
            this.loading = true
            this.errors = {
                agco: false,
                warehouse: false,
                products: false,
                imports: false,
            }

            const [agco, warehouses, products, imports] = await Promise.allSettled([
                http.get("/agco-logs/health?include_non_reporting=true&exclude_qa_seeded=true"),
                SalesFetchApi.getSalesByWarehouse(this.dateRange),
                SalesFetchApi.getSalesByProduct({ ...this.dateRange, limit: 50 }),
                http.get("/stock/entries"),
            ])

            if (agco.status === "fulfilled") {
                this.agcoStores = agco.value.data.result || []
                this.externalVerification = agco.value.data.externalVerification || null
            } else {
                this.agcoStores = []
                this.externalVerification = null
                this.errors.agco = true
            }

            if (warehouses.status === "fulfilled") {
                this.salesByWarehouse = warehouses.value.result || []
            } else {
                this.salesByWarehouse = []
                this.errors.warehouse = true
            }

            if (products.status === "fulfilled") {
                this.topProducts = products.value.result || []
            } else {
                this.topProducts = []
                this.errors.products = true
            }

            if (imports.status === "fulfilled") {
                this.importEntries = imports.value.data.result || []
            } else {
                this.importEntries = []
                this.errors.imports = true
            }

            this.loading = false
        },
        formatCurrency(value) {
            return this.formatCurrencyValue(value)
        },
        formatInteger(value) {
            return this.formatIntegerValue(value)
        },
        formatCurrencyValue(value) {
            return currencyFormatter.format(Number(value) || 0)
        },
        formatIntegerValue(value) {
            return integerFormatter.format(Number(value) || 0)
        },
        formatDate(value) {
            if (!value) return "Not available"
            const date = new Date(value)
            if (Number.isNaN(date.getTime())) return String(value)
            return date.toLocaleDateString("en-CA", {
                month: "short",
                day: "numeric",
                year: "numeric",
            })
        },
        formatDateTime(value) {
            if (!value) return "No local attempt"
            const date = new Date(value)
            if (Number.isNaN(date.getTime())) return String(value)
            return date.toLocaleString("en-CA", {
                month: "short",
                day: "numeric",
                year: "numeric",
                hour: "numeric",
                minute: "2-digit",
            })
        },
    },
}
</script>

<style lang="scss" scoped>
@use "sass:color";
@use "@/assets/variables.scss" as *;

.compliance-dashboard {
    color: $textColor;
    padding: $cardPaddingY;
}

.dashboard-page-header {
    align-items: flex-start;
    display: flex;
    gap: 18px;
    justify-content: space-between;
    margin-bottom: 16px;

    h1 {
        color: $primaryColor;
        font-size: 24px;
        font-weight: 800;
        line-height: 1.2;
        margin: 0;
    }
}

.dashboard-eyebrow {
    color: rgba($textColor, 0.56);
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0;
    margin: 0 0 4px;
    text-transform: uppercase;
}

.dashboard-summary {
    color: rgba($textColor, 0.68);
    font-size: 13px;
    line-height: 1.5;
    margin: 8px 0 0;
    max-width: 780px;
}

.dashboard-header-actions {
    align-items: flex-end;
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.dashboard-link,
.mini-link {
    align-items: center;
    color: $primaryColor;
    display: inline-flex;
    font-size: 13px;
    font-weight: 800;
    gap: 6px;
    text-decoration: none;
    white-space: nowrap;

    i {
        font-size: 18px;
    }

    &:hover {
        color: $secondaryColor;
        text-decoration: none;
    }
}

.source-pill,
.section-count {
    background: rgba($primaryColor, 0.08);
    border-radius: 999px;
    color: $primaryColor;
    font-size: 10px;
    font-weight: 800;
    line-height: 1;
    padding: 6px 9px;
    text-transform: uppercase;
    white-space: nowrap;
}

.verification-notice {
    align-items: flex-start;
    background: rgba($warningColor, 0.1);
    border: 1px solid rgba($warningColor, 0.28);
    border-radius: 8px;
    color: color.adjust($warningColor, $lightness: -28%);
    display: flex;
    gap: 10px;
    margin: 14px 0 0;
    padding: 12px 14px;

    i {
        font-size: 20px;
    }

    strong {
        font-size: 13px;
    }

    p {
        font-size: 12px;
        line-height: 1.4;
        margin: 3px 0 0;
    }
}

.status-grid {
    display: grid;
    gap: 10px;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    margin: 18px 0 12px;
}

.status-card {
    align-items: flex-start;
    background: $lightCardBg;
    border: 1px solid $lightBorderColor;
    border-left: 3px solid rgba($primaryColor, 0.3);
    border-radius: 8px;
    box-shadow: $cardShadow;
    display: flex;
    gap: 12px;
    min-height: 118px;
    padding: 14px;

    p {
        color: rgba($textColor, 0.62);
        font-size: 12px;
        font-weight: 700;
        margin: 0 0 7px;
    }

    h2 {
        color: $primaryColor;
        font-size: 24px;
        font-weight: 800;
        line-height: 1.05;
        margin: 0;
    }

    span {
        color: rgba($textColor, 0.64);
        display: block;
        font-size: 11px;
        line-height: 1.35;
        margin-top: 8px;
    }

    &.success {
        border-left-color: $successColor;
    }

    &.warning {
        border-left-color: $warningColor;
    }

    &.danger {
        border-left-color: $dangerColor;
    }
}

.status-icon {
    align-items: center;
    background: rgba($primaryColor, 0.08);
    border-radius: 8px;
    color: $primaryColor;
    display: flex;
    flex: 0 0 34px;
    height: 34px;
    justify-content: center;
    width: 34px;

    i {
        font-size: 19px;
    }
}

.dashboard-section {
    background: $lightCardBg;
    border: 1px solid $lightBorderColor;
    border-radius: 8px;
    box-shadow: $cardShadow;
    margin-bottom: 16px;
    padding: 16px;
}

.section-header {
    align-items: flex-start;
    display: flex;
    gap: 14px;
    justify-content: space-between;
    margin-bottom: 14px;

    h2 {
        color: $primaryColor;
        font-size: 17px;
        font-weight: 800;
        margin: 0;
    }
}

.visual-grid {
    display: grid;
    gap: 12px;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
}

.detail-grid {
    display: grid;
    gap: 12px;
    grid-template-columns: minmax(0, 1fr);
}

.visual-card {
    border: 1px solid $lightBorderColor;
    border-radius: 8px;
    min-width: 0;
    padding: 14px;
}

.visual-card-header {
    margin-bottom: 10px;

    h3 {
        color: $primaryColor;
        font-size: 15px;
        font-weight: 800;
        margin: 0 0 4px;
    }

    p {
        color: rgba($textColor, 0.62);
        font-size: 12px;
        line-height: 1.35;
        margin: 0;
    }
}

.visual-frame {
    height: 286px;
    min-width: 0;

    ::v-deep .echarts-horizontal-bar-chart {
        min-height: 286px;
    }
}

.loading-state,
.empty-state,
.inline-error {
    align-items: center;
    border: 1px solid $lightBorderColor;
    border-radius: 8px;
    color: rgba($textColor, 0.64);
    display: flex;
    font-size: 13px;
    min-height: 96px;
    padding: 14px;
}

.inline-error {
    background: rgba($dangerColor, 0.08);
    border-color: rgba($dangerColor, 0.18);
    color: color.adjust($dangerColor, $lightness: -8%);
}

.status-table {
    border-collapse: collapse;
    font-size: 13px;
    width: 100%;

    th {
        color: rgba($textColor, 0.62);
        font-size: 11px;
        font-weight: 800;
        padding: 8px;
        text-align: left;
        text-transform: uppercase;
    }

    td {
        border-top: 1px solid $lightBorderColor;
        color: rgba($textColor, 0.76);
        padding: 9px 8px;

        a {
            color: $primaryColor;
            font-weight: 800;
            text-decoration: none;
        }
    }
}

.log-links {
    white-space: nowrap;

    a {
        background: rgba($primaryColor, 0.08);
        border: 1px solid rgba($primaryColor, 0.16);
        border-radius: 6px;
        display: inline-flex;
        font-size: 11px;
        justify-content: center;
        line-height: 1;
        padding: 6px 8px;
    }

    a + a {
        margin-left: 6px;
    }
}

.detail-grid .cleanup-list {
    grid-template-columns: repeat(2, minmax(0, 1fr));
}

.status-badge {
    border-radius: 999px;
    display: inline-flex;
    font-size: 11px;
    font-weight: 800;
    line-height: 1;
    padding: 5px 8px;

    &.local_success {
        background: rgba($successColor, 0.14);
        color: color.adjust($successColor, $lightness: -14%);
    }

    &.failed {
        background: rgba($dangerColor, 0.12);
        color: color.adjust($dangerColor, $lightness: -8%);
    }

    &.overdue {
        background: rgba($warningColor, 0.14);
        color: color.adjust($warningColor, $lightness: -24%);
    }

    &.no_data {
        background: rgba($textColor, 0.08);
        color: rgba($textColor, 0.62);
    }

    &.expected {
        background: rgba($primaryColor, 0.1);
        color: $primaryColor;
    }

    &.not_configured {
        background: rgba($warningColor, 0.14);
        color: color.adjust($warningColor, $lightness: -24%);
    }

    &.not_applicable {
        background: rgba($textColor, 0.08);
        color: rgba($textColor, 0.62);
    }
}

.cleanup-list {
    display: grid;
    gap: 10px;
}

.cleanup-item {
    align-items: center;
    border: 1px solid $lightBorderColor;
    border-left: 3px solid rgba($primaryColor, 0.28);
    border-radius: 8px;
    color: inherit;
    display: flex;
    gap: 12px;
    justify-content: space-between;
    padding: 12px;
    text-decoration: none;

    strong {
        color: $primaryColor;
        display: block;
        font-size: 13px;
        margin-bottom: 4px;
    }

    p {
        color: rgba($textColor, 0.68);
        font-size: 12px;
        line-height: 1.35;
        margin: 0 0 7px;
    }

    span {
        color: rgba($textColor, 0.56);
        font-size: 11px;
        font-weight: 700;
    }

    i {
        color: $primaryColor;
        font-size: 18px;
    }

    &.success {
        border-left-color: $successColor;
    }

    &.warning {
        border-left-color: $warningColor;
    }

    &.danger {
        border-left-color: $dangerColor;
    }
}

.dark {
    color: $darkTextColor;

    .dashboard-page-header h1,
    .section-header h2,
    .visual-card-header h3,
    .status-card h2,
    .cleanup-item strong {
        color: $secondaryColorDark;
    }

    .dashboard-summary,
    .status-card span,
    .visual-card-header p,
    .cleanup-item p,
    .status-table td {
        color: rgba($darkTextColor, 0.72);
    }

    .dashboard-eyebrow,
    .status-table th,
    .cleanup-item span {
        color: rgba($darkTextColor, 0.62);
    }

    .dashboard-section,
    .status-card {
        background: $darkCardBg;
        border-color: $darkBorderColor;
    }

    .visual-card,
    .status-table td,
    .cleanup-item,
    .loading-state,
    .empty-state {
        border-color: $darkBorderColor;
    }

    .source-pill,
    .section-count,
    .status-icon {
        background: rgba($secondaryColorDark, 0.12);
        color: $secondaryColorDark;
    }

    .dashboard-link,
    .mini-link,
    .cleanup-item i,
    .status-table td a {
        color: $secondaryColorDark;
    }

    .log-links a {
        background: rgba($secondaryColorDark, 0.1);
        border-color: rgba($secondaryColorDark, 0.2);
    }
}

@media (max-width: 1100px) {
    .status-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .visual-grid,
    .detail-grid {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 768px) {
    .compliance-dashboard {
        padding: 12px;
    }

    .dashboard-page-header,
    .section-header {
        align-items: stretch;
        flex-direction: column;
    }

    .dashboard-header-actions {
        align-items: flex-start;
    }

    .status-grid {
        grid-template-columns: 1fr;
    }

    .detail-grid .cleanup-list {
        grid-template-columns: 1fr;
    }

    .status-table {
        display: block;
        overflow-x: auto;
        white-space: nowrap;
    }
}
</style>
